import * as React from 'react';
import { useState } from 'react';
import { connect } from 'react-redux';
import { Skeleton } from 'antd';
import { withUserType } from '../withUserType';
import { cn } from '../../utils/ClassNamesUtils';
import { OscarMark, OscarRoleProvider } from '../../ai_agent';
import { getOscarTheme } from '../../ai_agent/constants/AIAgentTheme';
import {
	fetchResolutionKnowledge,
	saveResolutionKnowledgeFeedback,
} from '../../thunks/work_orders_thunks';
import {
	FetchResolutionKnowledge,
	ResolutionKnowledgeFeedback,
	ResolutionKnowledgeIssue,
	ResolutionSummaryWorkOrder,
	SaveResolutionKnowledgeFeedback,
} from '../../types/resolutionKnowledge';
import { ResolutionFeedbackControls } from './ResolutionFeedbackControls';
import { useResolutionSummary } from './useResolutionSummary';

/** Dismissal: the card shrinks out, then the space it held closes up (design handoff timings). */
const SHRINK_MS = 120;
const LEAVE_MS = 480;

// Transforms are written out in full: `@tailwind base` is off in this repo, so the `--tw-rotate` /
// `--tw-scale-*` defaults the `translate-*` and `scale-*` utilities rely on are never defined, and
// those utilities produce an invalid `transform` that the browser drops.
const EASE = 'ease-[cubic-bezier(.645,.045,.355,1)]';

type LeavePhase = 'shrink' | 'collapse' | null;

interface WorkOrderResolutionSummaryProps {
	workOrder: ResolutionSummaryWorkOrder;
	/** False for a supplier only bidding on the work order: they must not see the summary. */
	enabled?: boolean;
	/** Injected: `withUserType` and `mapStateToProps`. */
	userType: string;
	userEmail: string;
	fetchKnowledge: FetchResolutionKnowledge;
	saveKnowledgeFeedback: SaveResolutionKnowledgeFeedback;
}

/**
 * Completion Summary — the AI resolution summary at the top of a completed work order's
 * Overview tab (OPE-5404). Always expanded: the one-line and detailed summaries, then Problem,
 * Cause and Recommendations, each only when the extraction provides it. Data and dismiss /
 * restore state come from `useResolutionSummary`, shared with the restore pill; renders nothing
 * when there is no summary or it was dismissed.
 */
const WorkOrderResolutionSummaryBase = ({
	workOrder,
	enabled = true,
	userType,
	userEmail,
	fetchKnowledge,
	saveKnowledgeFeedback,
}: WorkOrderResolutionSummaryProps) => {
	const { extraction, userFeedback, dismissed, restoring, dismiss, saveFeedback } =
		useResolutionSummary({
			workOrder,
			userEmail,
			enabled,
			fetchKnowledge,
			saveKnowledgeFeedback,
		});
	const [leaving, setLeaving] = useState<LeavePhase>(null);

	if (dismissed || (!restoring && !extraction)) return null;

	const theme = getOscarTheme(userType);
	const loading = restoring || !extraction;
	// Problem / Cause per issue and the recommendations show only when the extraction has them;
	// otherwise the card is just the two summaries. Arrays can be absent on schema-invalid or
	// quarantined runs.
	const issueRows = ((extraction && extraction.issues) || [])
		.map((issue) => ({ issue, rows: getIssueRows(issue) }))
		.filter((entry) => entry.rows.length > 0);
	// Case-specific recommendations recorded on the work order, not generated advice.
	const recommendations = ((extraction && extraction.recordedRecommendations) || [])
		.map((recommendation) => recommendation.description)
		.filter((description) => !!description);
	const hasDetails = issueRows.length > 0 || recommendations.length > 0;

	const handleDismiss = () => {
		setLeaving('shrink');
		setTimeout(() => setLeaving('collapse'), SHRINK_MS);
		setTimeout(() => {
			dismiss();
			setLeaving(null);
		}, LEAVE_MS);
	};

	return (
		<OscarRoleProvider userType={userType}>
			<div
				className={cn(
					'grid grid-cols-[minmax(0,1fr)] transition-[grid-template-rows] duration-300',
					EASE,
					leaving === 'collapse' ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
				)}
			>
				<div className={cn('min-h-0', leaving ? 'overflow-hidden' : 'overflow-visible')}>
					<section
						className={cn(
							'mb-m min-w-0 origin-top-right rounded transition-[transform,opacity] duration-300',
							EASE,
							theme.surface,
							theme.dashedStroke,
							leaving && 'opacity-0 [transform:translateY(-6px)_scale(0.94)]'
						)}
					>
						<div
							className={cn('flex min-h-6 items-center gap-s px-5', loading ? 'py-2.5' : 'py-3.5')}
						>
							<span className={cn('flex min-w-0 flex-1 items-center gap-xs', theme.accentText)}>
								<OscarMark size={26} className="shrink-0" />
								<span className="bodybold shrink-0 whitespace-nowrap uppercase tracking-wide">
									Completion Summary
								</span>
								{loading ? (
									<Skeleton.Input active size="small" block className="!w-full flex-1" />
								) : null}
							</span>
							{loading ? null : (
								<ResolutionFeedbackControls
									feedback={userFeedback}
									showPrompt={true}
									saveFeedback={saveFeedback}
									onDismiss={handleDismiss}
								/>
							)}
						</div>

						{loading ? null : (
							<div className="px-3 pb-l">
								<div className={cn('flex flex-col gap-l rounded bg-white p-m', theme.dashedStroke)}>
									<div className="flex flex-col gap-2xs">
										<SectionLabel>Summary</SectionLabel>
										<p className="bodymedium m-0 text-character-primary">
											{extraction.oneLinerSummary}
										</p>
										<p className="bodyregular m-0 whitespace-pre-line text-character-primary">
											{extraction.fullNarrativeSummary}
										</p>
									</div>
									{hasDetails ? (
										<div className="border-0 border-t border-solid border-grey-4" />
									) : null}
									{issueRows.length > 0 ? (
										<div className="flex flex-col gap-m">
											{issueRows.map((entry, index) => (
												<IssueBreakdown
													key={entry.issue.id}
													rows={entry.rows}
													heading={
														issueRows.length > 1
															? entry.issue.assetRef || `Issue ${index + 1}`
															: null
													}
												/>
											))}
										</div>
									) : null}
									{recommendations.length > 0 ? (
										<div className="flex flex-col gap-2xs">
											<SectionLabel>Recommendations</SectionLabel>
											{recommendations.map((recommendation) => (
												<p key={recommendation} className="bodyregular m-0 text-character-primary">
													{recommendation}
												</p>
											))}
										</div>
									) : null}
								</div>
							</div>
						)}
					</section>
				</div>
			</div>
		</OscarRoleProvider>
	);
};

interface DetailRowData {
	label: string;
	value: string;
}

/**
 * Problem: the reported symptom, or — for inspection findings, where the extraction leaves the
 * symptom null — what was observed. Cause: only when the record supports one; weaker assessments
 * are left out rather than stated.
 */
const getIssueRows = (issue: ResolutionKnowledgeIssue): DetailRowData[] => {
	const problem =
		issue.symptomsSummary ||
		(issue.observations || []).map((observation) => observation.description).join(' ');
	const assessment = issue.rootCauseAssessment;
	const cause = assessment && assessment.status === 'supported' ? assessment.causeDetail : null;
	return [
		{ label: 'Problem', value: problem },
		{ label: 'Cause', value: cause },
	].filter((row) => !!row.value);
};

interface DetailRowProps {
	label: string;
	children: React.ReactNode;
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
	<span className="footnoteMedium uppercase tracking-wide text-character-secondary">
		{children}
	</span>
);

const DetailRow = ({ label, children }: DetailRowProps) => (
	<div className="flex items-baseline gap-s">
		<span className="footnoteMedium w-16 shrink-0 whitespace-nowrap uppercase tracking-wide text-character-secondary">
			{label}
		</span>
		<span className="bodyregular min-w-0 flex-1 text-character-primary">{children}</span>
	</div>
);

interface IssueBreakdownProps {
	rows: DetailRowData[];
	heading: string | null;
}

const IssueBreakdown = ({ rows, heading }: IssueBreakdownProps) => (
	<div className="flex flex-col gap-xs">
		{heading ? (
			<span className="bodymedium capitalize text-character-primary">{heading}</span>
		) : null}
		{rows.map((row) => (
			<DetailRow key={row.label} label={row.label}>
				{row.value}
			</DetailRow>
		))}
	</div>
);

const mapStateToProps = (state) => ({
	userEmail: state.session.currentUser && state.session.currentUser.email,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchKnowledge: (workOrderId: number) =>
		dispatch(fetchResolutionKnowledge(ownProps.userType)(workOrderId)),
	saveKnowledgeFeedback: (workOrderId: number, feedback: ResolutionKnowledgeFeedback) =>
		dispatch(saveResolutionKnowledgeFeedback(ownProps.userType)(workOrderId, feedback)),
});

export const WorkOrderResolutionSummary = withUserType(
	connect(mapStateToProps, mapDispatchToProps)(WorkOrderResolutionSummaryBase)
);
